import { useRegisterEnvironmentCommands } from "@/actions/use-register-environment-commands";
import { VSCodeBrowserView } from "@/components/ai/chat/AgentExecutionConversation";
import type { EnvironmentViewMode } from "@/components/ai/ViewModeToggle";
import { EnvironmentBanner } from "@/components/environments/EnvironmentBanner";
import { EnvironmentDetailsTopHeader } from "@/components/environments/EnvironmentDetailsTopHeader";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/flexkit/ResizablePanel";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { cn } from "@/components/podkit/lib/cn";
import { MainContent } from "@/components/SidebarLayout";
import { VSCodeIpcProvider } from "@/contexts/VSCodeIpcContext";
import { useDynamicTitle } from "@/hooks/use-dynamic-title";
import { useEnsureVscodeServer } from "@/hooks/use-ensure-vscode-server";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { useEnvironmentConversation, getAgentExecution } from "@/hooks/use-environment-conversation";
import type { EnvironmentDetailsOutletContext } from "@/hooks/use-environment-details-outlet-context";
import { useEnvironmentViewMode } from "@/hooks/use-environment-view-mode";
import {
    useFlagCommandPaletteEnabled,
    useFlagOnaAIEnabled,
    useFlagOnaEnvironmentOnboardingEnabled,
    useFlagIsMobileVscodeEnabled,
    useFlagReviewCommentsEnabled,
    useFlagSuperOnaEnabled,
} from "@/hooks/use-flag";
import { useFollowMode, type FollowModeState } from "@/hooks/use-follow-mode";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useStreamEvents } from "@/hooks/use-stream-events";
import { useEditorURLQuery } from "@/queries/editor-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { useRunner } from "@/queries/runner-queries";
import { EnvironmentAgentExecutionConversation } from "@/routes/environments/details/EnvironmentAgentExecutionConversation";
import {
    EnvironmentChangesView,
    type EnvironmentChangesViewRef,
} from "@/routes/environments/details/EnvironmentChangesView";

import type { DrawerTab } from "@/routes/environments/details/EnvironmentBottomDrawer";
import { useIsWelcomeEligible } from "@/hooks/use-welcome-eligible";
import { hasLLMCapabilities } from "@/utils/runner-capabilities";

import type { VSCodeIPC } from "@/vscode-ipc";
import type React from "react";
import { Suspense, lazy, useCallback, useEffect, useId, useMemo, useRef, useState, type FC } from "react";
import type { ImperativePanelGroupHandle, ImperativePanelHandle } from "react-resizable-panels";
import { getPanelElement, getPanelGroupElement } from "react-resizable-panels";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { asNavState } from "@/routes/environments/details/environment-nav-state";

const LazyEnvironmentBottomDrawer = lazy(() =>
    import("@/routes/environments/details/EnvironmentBottomDrawer").then((m) => ({
        default: m.EnvironmentBottomDrawer,
    })),
);

export const EnvironmentDetailsLayout: FC = () => {
    useDynamicTitle("Environment");
    useEnsureVscodeServer();

    const { environmentId = "" } = useParams();
    const { value: commandPaletteEnabled } = useFlagCommandPaletteEnabled();
    const { data: environment } = useEnvironment(environmentId);
    const { data: runner } = useRunner(environment?.metadata?.runnerId);
    const scope = useMemo(() => ({ case: "environmentId" as const, value: environmentId }), [environmentId]);
    useStreamEvents(scope);
    const { environmentConversation } = useEnvironmentConversation(environmentId);
    const { value: isOnaEnvironmentOnboardingEnabled } = useFlagOnaEnvironmentOnboardingEnabled();
    const { value: isOnaEnabled } = useFlagOnaAIEnabled();

    const isFeatureFlagEnabled = isOnaEnabled || isOnaEnvironmentOnboardingEnabled;
    const runnerSupportsLLM = hasLLMCapabilities(runner);

    const shouldShowConversationView = isFeatureFlagEnabled && runnerSupportsLLM;

    if (shouldShowConversationView) {
        return (
            <VSCodeIpcProvider>
                {commandPaletteEnabled && <EnvironmentCommandPaletteRegistration environmentId={environmentId} />}
                <EnvironmentDetailsWithConversation
                    environmentConversation={environmentConversation}
                    environmentId={environmentId}
                />
            </VSCodeIpcProvider>
        );
    }

    // Show regular details page when feature flags are disabled OR runner lacks LLM capabilities
    return (
        <>
            {commandPaletteEnabled && <EnvironmentCommandPaletteRegistration environmentId={environmentId} />}
            <MainContent>
                <Suspense>
                    <VSCodeIpcProvider>
                        <EnvironmentDetailsLayoutOutlet context={{ showingAgentConversation: false }} />
                    </VSCodeIpcProvider>
                </Suspense>
            </MainContent>
        </>
    );
};

const EnvironmentCommandPaletteRegistration: FC<{ environmentId: string }> = ({ environmentId }) => {
    useRegisterEnvironmentCommands(environmentId);
    return null;
};

type EnvironmentAgentExecutionConversationPanelProps = {
    environmentConversation: ReturnType<typeof useEnvironmentConversation>["environmentConversation"];
    environmentId: string;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    followMode?: FollowModeState;
    onBuildButtonAppear?: (specFilePath: string | null) => void;
};

const EnvironmentAgentExecutionConversationPanel: FC<EnvironmentAgentExecutionConversationPanelProps> = ({
    environmentConversation,
    environmentId,
    isDetailsPanelOpen,
    onToggleDetailsPanel,
    followMode,
    onBuildButtonAppear,
}) => {
    const agentExecution = getAgentExecution(environmentConversation);

    return (
        <EnvironmentAgentExecutionConversation
            key={agentExecution?.id || `empty-${environmentId}`}
            agentExecution={agentExecution}
            isDetailsPanelOpen={isDetailsPanelOpen}
            onToggleDetailsPanel={onToggleDetailsPanel}
            followMode={followMode}
            environmentId={environmentId}
            onBuildButtonAppear={onBuildButtonAppear}
        />
    );
};

const EnvironmentDetailsPanel: FC<{
    environmentId: string;
    showConversationPanel: boolean;
    onToggleDetailsPanel?: () => void;
    onToggleConversationPanel?: () => void;
    isConversationPanelOpen?: boolean;
    onViewModeChange?: (mode: EnvironmentViewMode) => void;
    followMode?: ReturnType<typeof useFollowMode>;
    onCurrentViewMode?: (mode: EnvironmentViewMode) => void;
    changesViewRef?: React.RefObject<EnvironmentChangesViewRef>;
    /** File path queued for preview — consumed by CodeChangesPanel on mount */
    pendingSpecPreview?: string | null;
    onPendingSpecPreviewConsumed?: () => void;
    /** Parent-driven view mode change (e.g. Build button switching to changes tab) */
    requestedViewMode?: EnvironmentViewMode | null;
    onRequestedViewModeConsumed?: () => void;
}> = ({
    environmentId,
    showConversationPanel,
    onToggleDetailsPanel,
    onToggleConversationPanel,
    isConversationPanelOpen = true,
    onViewModeChange,
    followMode,
    onCurrentViewMode,
    changesViewRef,
    pendingSpecPreview,
    onPendingSpecPreviewConsumed,
    requestedViewMode,
    onRequestedViewModeConsumed,
}) => {
    const { data: environment } = useEnvironment(environmentId);
    const { data: runner } = useRunner(environment?.metadata?.runnerId);
    const [viewMode, setViewMode] = useEnvironmentViewMode(environment);
    const vscodeIpcRef = useRef<VSCodeIPC | null>(null);
    const { value: isReviewCommentsEnabled } = useFlagReviewCommentsEnabled();
    const { value: isSuperOnaEnabled } = useFlagSuperOnaEnabled();

    // Notify parent of current view mode
    useEffect(() => {
        onCurrentViewMode?.(viewMode);
    }, [viewMode, onCurrentViewMode]);

    useEffect(() => {
        if (!requestedViewMode) return;
        setViewMode(requestedViewMode);
        onRequestedViewModeConsumed?.();
    }, [requestedViewMode, setViewMode, onRequestedViewModeConsumed]);

    const { availableEditors } = useGetEditorForEnv(environment?.id);
    const isVSCodeBrowserAllowed = useMemo(() => {
        const hasVSCodeBrowser = availableEditors?.some((editor) => editor.alias === "vscode-browser") ?? false;
        const isVSCodeContext = !!window.isWebview;
        return hasVSCodeBrowser && !isVSCodeContext;
    }, [availableEditors]);
    const vscodeBrowserId = useMemo(
        () => availableEditors?.find((editor) => editor.alias === "vscode-browser")?.id,
        [availableEditors],
    );
    const editorUrlQuery = useEditorURLQuery({
        editorId: vscodeBrowserId,
        environment: environment,
    });
    const editorUrl = editorUrlQuery.data;

    const [readyToLoadVSCode, setReadyToLoadVSCode] = useState(false);

    useEffect(() => {
        // Always load VS Code when environment is available and VS Code is allowed
        if (environment && isVSCodeBrowserAllowed && !readyToLoadVSCode) {
            setReadyToLoadVSCode(true);
        }
    }, [environment, isVSCodeBrowserAllowed, readyToLoadVSCode]);

    useEffect(() => {
        if (viewMode === "vscode" && !isVSCodeBrowserAllowed) {
            setViewMode("details");
        }
    }, [viewMode, isVSCodeBrowserAllowed, setViewMode]);

    // Switch view mode when navigating via command palette or other triggers.
    const location = useLocation();
    const navState = asNavState(location.state);
    useEffect(() => {
        if (navState?.switchToVscode) {
            setViewMode("vscode");
        } else if (navState?.switchToDetails) {
            setViewMode("details");
        } else {
            return;
        }
        // Clear state so browser back/forward doesn't re-trigger.
        window.history.replaceState({}, "", location.pathname);
    }, [navState?.switchToDetails, navState?.switchToVscode, setViewMode, location.pathname]);

    const _onViewModeChange = useCallback(
        (mode: EnvironmentViewMode) => {
            setViewMode(mode);
            if (onViewModeChange) {
                onViewModeChange(mode);
            }
        },
        [setViewMode, onViewModeChange],
    );

    useEffect(() => {
        if (!followMode) return;

        if (followMode.enabled) {
            setViewMode("vscode");
        }

        if (!vscodeIpcRef.current) return;
        if (followMode.enabled) {
            void vscodeIpcRef.current.enableFollowMode();
        } else {
            void vscodeIpcRef.current.disableFollowMode();
        }
    }, [followMode, setViewMode, vscodeIpcRef]);

    const mainContent = (
        <>
            {viewMode === "details" && (
                <ScrollArea
                    orientation="vertical"
                    className={cn("mx-[2px] my-[2px] gap-6 p-4 md:p-6", isSuperOnaEnabled ? "min-h-0 flex-1" : "h-full")}
                >
                    <Suspense>
                        <EnvironmentDetailsLayoutOutlet
                            context={{
                                showingAgentConversation: showConversationPanel,
                            }}
                        />
                    </Suspense>
                </ScrollArea>
            )}

            {viewMode === "changes" && (
                <ScrollArea
                    orientation="vertical"
                    className={cn(
                        "mx-[2px] my-[2px] flex flex-col gap-6 p-4 md:p-6",
                        isSuperOnaEnabled ? "min-h-0 flex-1" : "h-full",
                    )}
                >
                    <EnvironmentChangesView
                        ref={changesViewRef}
                        environmentId={environmentId}
                        enableReviewComments={isReviewCommentsEnabled}
                        pendingSpecPreview={pendingSpecPreview}
                        onPendingSpecPreviewConsumed={onPendingSpecPreviewConsumed}
                    />
                </ScrollArea>
            )}

            <div
                className={cn(
                    "size-full grow flex-col border-b border-l-0 border-r border-t-0 border-border-base",
                    viewMode === "vscode" ? "flex" : "hidden",
                )}
                data-vscode-visible={viewMode === "vscode"}
            >
                {readyToLoadVSCode && (
                    <VSCodeBrowserView
                        editorUrl={editorUrl?.url}
                        className="grow border-none"
                        followMode={followMode}
                        visible={viewMode === "vscode"}
                    />
                )}
            </div>
        </>
    );

    return (
        <div className="flex h-full max-h-full flex-col">
            {!window.isWebview && (
                <EnvironmentDetailsTopHeader
                    environment={environment}
                    viewMode={viewMode}
                    onViewModeChange={_onViewModeChange}
                    onToggleDetailsPanel={onToggleDetailsPanel}
                    onToggleConversationPanel={onToggleConversationPanel}
                    isConversationPanelOpen={isConversationPanelOpen}
                    isVSCodeBrowserAllowed={isVSCodeBrowserAllowed}
                />
            )}
            <EnvironmentBanner environment={environment} runner={runner} />
            {isSuperOnaEnabled ? (
                <BottomDrawerLayout environmentId={environmentId}>{mainContent}</BottomDrawerLayout>
            ) : (
                mainContent
            )}
        </div>
    );
};

// Tab bar height in pixels — NavigationTabs.List md size (h-10 = 40px)
const DRAWER_HEADER_PX = 40;

const BottomDrawerLayout: FC<{ environmentId: string; children: React.ReactNode }> = ({
    environmentId,
    children,
}) => {
    const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
    const panelGroupId = useId();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [activeTab, setActiveTab] = useState<DrawerTab>("ports-services");
    const expandedSizeRef = useRef(30);

    const handleToggle = useCallback(() => {
        const group = panelGroupRef.current;
        const el = getPanelGroupElement(panelGroupId);
        if (!group || !el) return;

        if (isCollapsed) {
            group.setLayout([100 - expandedSizeRef.current, expandedSizeRef.current]);
            setIsCollapsed(false);
        } else {
            const layout = group.getLayout();
            if (layout[1] > 5) {
                expandedSizeRef.current = layout[1];
            }
            const headerPct = (DRAWER_HEADER_PX / el.clientHeight) * 100;
            group.setLayout([100 - headerPct, headerPct]);
            setIsCollapsed(true);
        }
    }, [isCollapsed, panelGroupId]);

    const drawerProps = {
        environmentId,
        isCollapsed,
        activeTab,
        onTabChange: setActiveTab,
        onToggle: handleToggle,
    };

    return (
        <ResizablePanelGroup
            id={panelGroupId}
            ref={panelGroupRef}
            direction="vertical"
            className="min-h-0 flex-1 [&>div[data-panel]]:transition-[flex-basis] [&>div[data-panel]]:duration-150 [&>div[data-panel]]:ease-out"
            autoSaveId="environment-bottom-drawer-v2"
        >
            <ResizablePanel defaultSize={70} minSize={20} order={1}>
                <div className="flex h-full flex-col">{children}</div>
            </ResizablePanel>
            <ResizableHandle
                disabled={isCollapsed}
                className={cn(
                    "h-0 w-full border-l-0 border-t border-border-base",
                    !isCollapsed && "hover:border-border-strong active:border-border-strong",
                )}
            />
            <ResizablePanel defaultSize={30} minSize={1} maxSize={60} order={2}>
                <div className="flex h-full flex-col overflow-hidden">
                    <Suspense>
                        <LazyEnvironmentBottomDrawer {...drawerProps} />
                    </Suspense>
                </div>
            </ResizablePanel>
        </ResizablePanelGroup>
    );
};

const EnvironmentDetailsWithConversation: FC<{
    environmentConversation: ReturnType<typeof useEnvironmentConversation>["environmentConversation"];
    environmentId: string;
}> = ({ environmentConversation, environmentId }) => {
    // Simple rule: track if code view is expanded per environment
    // Expanded code view = conversation hidden, everything else = conversation visible
    const getCodeViewExpanded = useCallback(() => {
        const key = `environment-code-expanded:${environmentId}`;
        return localStorage.getItem(key) === "true";
    }, [environmentId]);

    const setCodeViewExpanded = useCallback(
        (expanded: boolean) => {
            const key = `environment-code-expanded:${environmentId}`;
            localStorage.setItem(key, String(expanded));
        },
        [environmentId],
    );

    const [isConversationPanelCollapsed, setIsConversationPanelCollapsed] = useState(false);
    const showConversationPanel = !isConversationPanelCollapsed;
    const [currentViewMode, setCurrentViewMode] = useState<EnvironmentViewMode>("details");

    const [fixedConversationPanelSize, setFixedConversationPanelSize] = useState(0);
    const conversationPanelRef = useRef<ImperativePanelHandle>(null);
    const changesViewRef = useRef<EnvironmentChangesViewRef>(null);
    const panelGroupId = useId();
    const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
    const panelRef = useRef<ImperativePanelHandle>(null);
    const isWelcomeEligible = useIsWelcomeEligible();
    const [isDetailsPanelCollapsed, setIsDetailsPanelCollapsed] = useState(false);
    const hasAutoCollapsedForWelcome = useRef(false);

    // Collapse the details panel for welcome-eligible users.
    // Uses requestAnimationFrame to run after the ResizablePanel has finished
    // its initial layout (which fires onExpand and would override setState).
    useEffect(() => {
        if (isWelcomeEligible && !hasAutoCollapsedForWelcome.current && panelRef.current) {
            hasAutoCollapsedForWelcome.current = true;
            requestAnimationFrame(() => {
                panelRef.current?.collapse();
            });
        }
    }, [isWelcomeEligible]);
    const updatePanelSizeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const followMode = useFollowMode();

    const conversationPanelSize = showConversationPanel
        ? { defaultSize: 70, minSize: 30 }
        : { defaultSize: 0, minSize: 0 };

    const { isMobileViewport: isMobile } = useIsMobileViewport();
    const { value: isMobileVscodeEnabled } = useFlagIsMobileVscodeEnabled();

    // The library doesn't support animating the collapse/expand transition of panels [1]
    // You achieve it using CSS transitions, however, that breaks drag resizing behavior/performance
    // so the workaround is to only add those transitions when not dragging.
    //
    // [1] https://github.com/bvaughn/react-resizable-panels/issues/310#issuecomment-1974779047
    const [isDragging, setIsDragging] = useState(false);

    useEffect(
        () => () => {
            if (updatePanelSizeTimeout.current) {
                clearTimeout(updatePanelSizeTimeout.current);
            }
        },
        [],
    );

    useEffect(() => {
        if (!followMode) return;

        if (followMode.enabled) {
            panelRef.current?.expand();
        }
    }, [followMode, panelRef]);

    // Ensure conversation is always visible when in details or changes mode
    useEffect(() => {
        if ((currentViewMode === "details" || currentViewMode === "changes") && isConversationPanelCollapsed) {
            if (conversationPanelRef.current?.isCollapsed()) {
                conversationPanelRef.current.expand();
            }
            setIsConversationPanelCollapsed(false);
        }
    }, [currentViewMode, isConversationPanelCollapsed]);

    const handleToggleDetailsPanel = useCallback(() => {
        if (!panelRef.current) {
            return;
        }

        setFixedConversationPanelSize(0);

        if (panelRef.current.isCollapsed()) {
            panelRef.current.expand();
        } else {
            panelRef.current.collapse();
            followMode?.setEnabled(false);
        }
    }, [panelRef, followMode]);

    const handleToggleConversationPanel = useCallback(() => {
        if (!conversationPanelRef.current) {
            return;
        }

        const willBeExpanded = conversationPanelRef.current.isCollapsed();

        if (willBeExpanded) {
            // Minimizing code view - show conversation
            conversationPanelRef.current.expand();
            setIsConversationPanelCollapsed(false);
            setCodeViewExpanded(false);
        } else {
            // Expanding code view - hide conversation
            conversationPanelRef.current.collapse();
            setIsConversationPanelCollapsed(true);
            setCodeViewExpanded(true);
        }
    }, [setCodeViewExpanded]);

    const onViewModeChange = useCallback(
        (mode: EnvironmentViewMode) => {
            setCurrentViewMode(mode);
            switch (mode) {
                case "details":
                case "changes": {
                    followMode?.setEnabled(false);
                    // Switching to details/changes view = minimizing code view = show conversation
                    if (conversationPanelRef.current?.isCollapsed()) {
                        conversationPanelRef.current.expand();
                        setIsConversationPanelCollapsed(false);
                    }
                    setCodeViewExpanded(false);
                    break;
                }
                case "vscode": {
                    // Switching to code view - restore per-environment expanded state
                    const isExpanded = getCodeViewExpanded();
                    const isCurrentlyCollapsed = conversationPanelRef.current?.isCollapsed() ?? false;

                    if (isExpanded && !isCurrentlyCollapsed) {
                        // Should be expanded (conversation hidden)
                        conversationPanelRef.current?.collapse();
                        setIsConversationPanelCollapsed(true);
                    } else if (!isExpanded && isCurrentlyCollapsed) {
                        // Should not be expanded (conversation visible)
                        conversationPanelRef.current?.expand();
                        setIsConversationPanelCollapsed(false);
                    }
                    break;
                }
            }

            const id = conversationPanelRef.current?.getId();
            const conversationPanelElement = document.getElementById(id ?? "");
            if (mode === "vscode" && conversationPanelElement) {
                setFixedConversationPanelSize(conversationPanelElement.clientWidth);
                // wait for sidebar to finish animating out, then calculate update the panel proportion imperatively
                updatePanelSizeTimeout.current = setTimeout(() => {
                    const groupElement = getPanelGroupElement(panelGroupId);
                    const id = conversationPanelRef.current?.getId();
                    const conversationPanelElement = getPanelElement(id ?? "");
                    if (groupElement && conversationPanelElement && panelGroupRef.current) {
                        const proportionalSize =
                            (conversationPanelElement.clientWidth / groupElement.clientWidth) * 100;
                        panelGroupRef.current.setLayout([proportionalSize, 100 - proportionalSize]);
                    }
                }, 250);
            }
        },
        [followMode, getCodeViewExpanded, setCodeViewExpanded, panelGroupId],
    );

    const [pendingSpecPreview, setPendingSpecPreview] = useState<string | null>(null);
    const clearPendingSpecPreview = useCallback(() => setPendingSpecPreview(null), []);

    const [requestedViewMode, setRequestedViewMode] = useState<EnvironmentViewMode | null>(null);
    const clearRequestedViewMode = useCallback(() => setRequestedViewMode(null), []);

    const handleBuildButtonAppear = useCallback(
        (specFilePath: string | null) => {
            if (isMobile) return;

            if (panelRef.current?.isCollapsed()) {
                panelRef.current.expand();
            }

            onViewModeChange("changes");
            setRequestedViewMode("changes");

            if (specFilePath) {
                setPendingSpecPreview(specFilePath);
            }
        },
        [isMobile, onViewModeChange, panelRef],
    );

    const onDragging = (isDragging: boolean) => {
        setIsDragging(isDragging);
        if (fixedConversationPanelSize) {
            setFixedConversationPanelSize(0);
        }
    };

    // Only auto-collapse details panel on mobile when mobile VSCode is disabled
    useEffect(() => {
        if (isMobile && !isMobileVscodeEnabled) {
            setIsDetailsPanelCollapsed(true);
        }
    }, [isMobile, isMobileVscodeEnabled]);

    return (
        <MainContent noPadding>
            {isMobile ? (
                isDetailsPanelCollapsed ? (
                    <EnvironmentAgentExecutionConversationPanel
                        environmentConversation={environmentConversation}
                        environmentId={environmentId}
                        isDetailsPanelOpen={false}
                        onToggleDetailsPanel={() => {
                            setIsDetailsPanelCollapsed(false);
                        }}
                        followMode={followMode}
                        onBuildButtonAppear={handleBuildButtonAppear}
                    />
                ) : (
                    <EnvironmentDetailsPanel
                        environmentId={environmentId}
                        showConversationPanel={showConversationPanel}
                        onToggleDetailsPanel={() => {
                            setIsDetailsPanelCollapsed(true);
                        }}
                        onToggleConversationPanel={() => {
                            setIsDetailsPanelCollapsed(false);
                        }}
                        isConversationPanelOpen={!isDetailsPanelCollapsed}
                        followMode={followMode}
                        onCurrentViewMode={setCurrentViewMode}
                        changesViewRef={changesViewRef}
                        pendingSpecPreview={pendingSpecPreview}
                        onPendingSpecPreviewConsumed={clearPendingSpecPreview}
                        requestedViewMode={requestedViewMode}
                        onRequestedViewModeConsumed={clearRequestedViewMode}
                    />
                )
            ) : (
                <ResizablePanelGroup
                    id={panelGroupId}
                    ref={panelGroupRef}
                    direction="horizontal"
                    className="flex"
                    autoSaveId="environment-details-layout"
                >
                    <ResizablePanel
                        ref={conversationPanelRef}
                        defaultSize={conversationPanelSize.defaultSize}
                        minSize={conversationPanelSize.minSize}
                        collapsedSize={0}
                        collapsible={currentViewMode === "vscode"}
                        order={1}
                        className={cn({
                            // See definition of isDragging for why we're doing this selectively
                            "transition-[flex-basis,flex-grow,flex-shrink] duration-100 ease-in-out": !isDragging,
                        })}
                        style={
                            fixedConversationPanelSize
                                ? {
                                      maxWidth: `${fixedConversationPanelSize}px`,
                                  }
                                : {}
                        }
                        onCollapse={() => setIsConversationPanelCollapsed(true)}
                        onExpand={() => setIsConversationPanelCollapsed(false)}
                        id="conversation-panel"
                    >
                        <EnvironmentAgentExecutionConversationPanel
                            environmentConversation={environmentConversation}
                            environmentId={environmentId}
                            isDetailsPanelOpen={!isDetailsPanelCollapsed}
                            onToggleDetailsPanel={handleToggleDetailsPanel}
                            followMode={followMode}
                            onBuildButtonAppear={handleBuildButtonAppear}
                        />
                    </ResizablePanel>
                    <ResizableHandle
                        disabled={isConversationPanelCollapsed || isDetailsPanelCollapsed}
                        onDragging={onDragging}
                    />
                    <ResizablePanel
                        defaultSize={100 - conversationPanelSize.defaultSize}
                        minSize={30}
                        collapsedSize={0}
                        ref={panelRef}
                        collapsible
                        className={cn({
                            // See definition of isDragging for why we're doing this selectively
                            "transition-[flex-basis,flex-grow,flex-shrink] duration-100 ease-in-out": !isDragging,
                        })}
                        onCollapse={() => setIsDetailsPanelCollapsed(true)}
                        onExpand={() => setIsDetailsPanelCollapsed(false)}
                        order={2}
                        id="environment-details"
                    >
                        <EnvironmentDetailsPanel
                            environmentId={environmentId}
                            showConversationPanel={showConversationPanel}
                            onViewModeChange={onViewModeChange}
                            onToggleConversationPanel={handleToggleConversationPanel}
                            onToggleDetailsPanel={handleToggleDetailsPanel}
                            isConversationPanelOpen={!isConversationPanelCollapsed}
                            followMode={followMode}
                            onCurrentViewMode={setCurrentViewMode}
                            changesViewRef={changesViewRef}
                            pendingSpecPreview={pendingSpecPreview}
                            onPendingSpecPreviewConsumed={clearPendingSpecPreview}
                            requestedViewMode={requestedViewMode}
                            onRequestedViewModeConsumed={clearRequestedViewMode}
                        />
                    </ResizablePanel>
                </ResizablePanelGroup>
            )}
        </MainContent>
    );
};

/**
 * Outlet wrapper for EnvironmentDetailsOutletContext to ensure type-safety if we update the context type.
 */
const EnvironmentDetailsLayoutOutlet: FC<{ context: EnvironmentDetailsOutletContext }> = ({ context }) => {
    return <Outlet context={context} />;
};
